import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import ContextWindowMeter from "../common/ContextWindowMeter";
import type { ArchitectChatMessage } from "../../hooks/useArchitectAgentChat";
import type { AgentContextUsage } from "../../types/agentChat";
import {
  GUIDE_FILE_ACCEPT,
  readGuideFile,
  buildIntakeRequirementMessage,
  type GuideFileReadResult,
} from "../../utils/guideFileReader";

interface ArchitectChatPanelProps {
  messages: ArchitectChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  error: string | null;
  contextUsage?: AgentContextUsage | null;
  quickPrompts?: string[];
  maximized?: boolean;
  onToggleMaximize?: () => void;
  enableFileAttach?: boolean;
  title?: string;
  placeholder?: string;
  thinkingLabel?: string;
}

const ArchitectChatPanel: React.FC<ArchitectChatPanelProps> = ({
  messages,
  onSend,
  loading,
  error,
  contextUsage = null,
  quickPrompts = [],
  maximized = false,
  onToggleMaximize,
  enableFileAttach = false,
  title = "Architect chat",
  placeholder = "Ask the architect agent…",
  thinkingLabel = "Architect is thinking…",
}) => {
  const [input, setInput] = React.useState("");
  const [files, setFiles] = React.useState<GuideFileReadResult[]>([]);
  const [converting, setConverting] = React.useState(false);
  const [convertError, setConvertError] = React.useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const submitText = (text: string, attachments: GuideFileReadResult[] = files) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(
      enableFileAttach
        ? buildIntakeRequirementMessage(trimmed, attachments)
        : trimmed,
    );
  };

  const handleSend = () => {
    if (!input.trim() || converting) return;
    submitText(input);
    setInput("");
    setFiles([]);
    setConvertError(null);
  };

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (picked.length === 0) return;
    setConverting(true);
    setConvertError(null);
    try {
      const converted: GuideFileReadResult[] = [];
      for (const file of picked) {
        converted.push(await readGuideFile(file));
      }
      setFiles((prev) => [...prev, ...converted]);
    } catch (err: unknown) {
      setConvertError(
        err instanceof Error
          ? err.message
          : "Could not convert attachment to Markdown",
      );
    } finally {
      setConverting(false);
    }
  };

  const busy = loading || converting;
  const panelHeight = maximized
    ? "calc(100vh - 220px)"
    : { xs: 480, md: "min(65vh, 640px)" };

  return (
    <Paper
      variant="outlined"
      sx={{ display: "flex", flexDirection: "column", height: panelHeight, minHeight: maximized ? 400 : undefined }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ContextWindowMeter contextUsage={contextUsage} />
          {onToggleMaximize && (
          <Tooltip title={maximized ? "Restore layout" : "Maximize chat"}>
            <IconButton
              size="small"
              onClick={onToggleMaximize}
              aria-label={maximized ? "Restore layout" : "Maximize chat"}
            >
              {maximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <AgentChatMessageBubble
              role={m.role}
              content={m.displayContent ?? m.content}
              structuredBlockPreset={m.role === "assistant" ? "architect" : undefined}
            />
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="caption" color="text.secondary">
              {thinkingLabel}
            </Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      {(error || convertError) && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {convertError || error}
        </Alert>
      )}
      {quickPrompts.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {quickPrompts.map((p) => (
            <Typography
              key={p}
              component="button"
              variant="caption"
              onClick={() => submitText(p, [])}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1,
                py: 0.25,
                cursor: "pointer",
                bgcolor: "background.paper",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {p}
            </Typography>
          ))}
        </Box>
      )}
      {enableFileAttach && files.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {files.map((f, i) => (
            <Chip
              key={`${f.fileName}-${i}`}
              size="small"
              label={f.fileName}
              onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1, p: 2, borderTop: 1, borderColor: "divider", alignItems: "flex-end" }}>
        {enableFileAttach && (
          <Tooltip title="Attach related docs (text, Markdown, JSON, YAML, or convert via CMS)">
            <IconButton component="label" disabled={busy} aria-label="Attach related documents">
              <AttachFileIcon />
              <input
                type="file"
                hidden
                multiple
                accept={GUIDE_FILE_ACCEPT}
                onChange={handleAttach}
              />
            </IconButton>
          </Tooltip>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
          disabled={busy}
        />
        <IconButton color="primary" onClick={handleSend} disabled={busy || !input.trim()}>
          {converting ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ArchitectChatPanel;
